import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  faFile,
  faImage,
  faPalette,
  faPlusCircle,
  faTableLayout,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import classNames from "classnames";
import "./websiteBuilderToolbar.scss";
import { WebsiteBuilderToolbarMenu, TWebsiteBuilderToolbarMenuType } from "./menu/WebsiteBuilderToolbarMenu";
import {
  IWebsiteBuilderToolbarState,
  THandleSetToolbarMenuType,
} from "../hooks/toolbarState/useWebsiteBuilderToolbarState";
import { WebsiteBuilderToolbarContext } from "./websiteBuilderToolbarContext";
import { websiteBuilderZLayers } from "../websiteBuilderZLayers";
import { WEBSITE_BUILDER_PORTAL } from "../WebsiteBuilderEditor";

interface IWebsiteBuilderToolbarProps {
  toolbarState: IWebsiteBuilderToolbarState;
  handleSetToolbarMenuType: THandleSetToolbarMenuType;
}

interface IWebsiteBuilderToolbarMenuOption {
  menuType: TWebsiteBuilderToolbarMenuType;
  tooltip: string;
  icon: IconDefinition;
}

export function WebsiteBuilderToolbar(props: IWebsiteBuilderToolbarProps) {
  const { toolbarState, handleSetToolbarMenuType } = props;
  const { t } = useTranslation();

  const toolbarPanelRef = useRef<HTMLDivElement>(null);
  const toolbarMenuPanelRef = useRef<HTMLDivElement>(null);

  const toolbarMenuOptions: IWebsiteBuilderToolbarMenuOption[] = useMemo(
    () => [
      {
        menuType: "add-elements",
        tooltip: t("Add Elements"),
        icon: faPlusCircle,
      },
      {
        menuType: "add-page",
        tooltip: t("Add Page"),
        icon: faFile,
      },
      {
        menuType: "add-section",
        tooltip: t("Add Section"),
        icon: faTableLayout,
      },
      {
        menuType: "design",
        tooltip: t("Design"),
        icon: faPalette,
      },
      {
        menuType: "media",
        tooltip: t("Media"),
        icon: faImage,
      },
    ],
    [],
  );

  function handlePointerDownOutsidePanels(e: PointerEvent) {
    const portal = document.getElementById(WEBSITE_BUILDER_PORTAL);

    if (
      toolbarState.menuType === "none" ||
      toolbarPanelRef.current == null ||
      toolbarMenuPanelRef.current == null ||
      !(e.target instanceof Node) ||
      toolbarPanelRef.current.contains(e.target) ||
      toolbarMenuPanelRef.current.contains(e.target) ||
      portal?.contains(e.target)
    ) {
      return;
    }

    handleSetToolbarMenuType("none");
  }

  useEffect(() => {
    document.addEventListener("pointerdown", handlePointerDownOutsidePanels, { capture: true });

    return () => document.removeEventListener("pointerdown", handlePointerDownOutsidePanels, { capture: true });
  }, [toolbarState.menuType, handleSetToolbarMenuType]);

  return (
    <WebsiteBuilderToolbarContext.Provider
      value={{
        toolbarState,
        handleSetToolbarMenuType,
      }}
    >
      <div
        ref={toolbarPanelRef}
        className="website-builder-toolbar-panel"
        style={{
          zIndex: websiteBuilderZLayers.shell.ui,
        }}
      >
        {toolbarMenuOptions.map(toolbarMenuOption => {
          const isSelectedMenuType = toolbarMenuOption.menuType === toolbarState.menuType;

          return (
            <OverlayTrigger
              key={toolbarMenuOption.menuType}
              placement="right"
              show={isSelectedMenuType ? false : undefined}
              delay={{
                show: 500,
                hide: 100,
              }}
              overlay={
                <Tooltip id={`website-builder-toolbar-menu-option-tooltip-${toolbarMenuOption.menuType}`}>
                  {toolbarMenuOption.tooltip}
                </Tooltip>
              }
            >
              <FontAwesomeIcon
                className={classNames("website-builder-toolbar-menu-option", {
                  "website-builder-toolbar-menu-option-selected": isSelectedMenuType && !toolbarState.closingMenuPanel,
                })}
                icon={toolbarMenuOption.icon}
                onClick={() => handleSetToolbarMenuType(isSelectedMenuType ? "none" : toolbarMenuOption.menuType)}
              />
            </OverlayTrigger>
          );
        })}
      </div>
      <div
        ref={toolbarMenuPanelRef}
        className={classNames("website-builder-toolbar-menu-panel", {
          open: toolbarState.menuType !== "none" && !toolbarState.closingMenuPanel,
          closed: toolbarState.menuType === "none" || toolbarState.closingMenuPanel,
        })}
        style={{
          zIndex: websiteBuilderZLayers.shell.underlay,
        }}
      >
        <WebsiteBuilderToolbarMenu />
      </div>
    </WebsiteBuilderToolbarContext.Provider>
  );
}
